import { WorkflowRun } from '@digital-twins/shared-types'

import { Text } from '@/components/ui/Text'
import { TwinsMarkLoader } from '@/components/quick-study-results/TwinsMarkLoader'
import { cn } from '@/lib/utils'

import {
  deriveThreadRunActivityLabel,
  formatThreadRunActivityLabel,
} from './deriveThreadRunActivityLabel'
import { readTwinTestSamplingStems } from './readTwinTestSamplingStems'
import { ThreadStepGlyph } from './ThreadStepGlyph'

interface ThreadRunActivityIndicatorProps {
  run: WorkflowRun | null
  isStartingRun?: boolean
  language?: string | null
}

function formatSamplingProgress(
  progress: { completed: number | null; total: number | null } | null,
): string | null {
  if (progress == null) {
    return null
  }
  if (progress.completed == null || progress.total == null) {
    return null
  }
  return `· ${progress.completed} / ${progress.total}`
}

export function samplingStepperActiveIndex(params: {
  stems: Array<string>
  questionIndex: number | null | undefined
}): number | null {
  const { stems, questionIndex } = params
  if (stems.length < 2) {
    return null
  }
  if (questionIndex == null || !Number.isInteger(questionIndex)) {
    return null
  }

  return Math.min(Math.max(questionIndex, 0), stems.length - 1)
}

/**
 * In-thread busy line for workflow run phases (start / prepare / sample).
 * Prompt and report lines use the live step title; sampling keeps the twins
 * placeholder. Shares TwinsMarkLoader + muted Text with ThreadActivityIndicator.
 *
 * Sampling with two or more known questions and a `questionIndex` becomes a
 * connected stepper so a 50/50 → 0/50 reset does not hide earlier questions.
 */
export function ThreadRunActivityIndicator(
  props: ThreadRunActivityIndicatorProps,
) {
  const { run, isStartingRun, language } = props

  const activity = deriveThreadRunActivityLabel(run, { isStartingRun })

  if (activity === null) {
    return null
  }

  const label = formatThreadRunActivityLabel(activity, run, language)
  const counts = formatSamplingProgress(activity.progress)
  const stems = readTwinTestSamplingStems(run)
  const activeIndex =
    activity.phase === 'sampling'
      ? samplingStepperActiveIndex({
          stems,
          questionIndex: activity.progress?.questionIndex,
        })
      : null
  const showStepper = activeIndex !== null
  const ariaLabel = showStepper
    ? `${label} ${activeIndex + 1} / ${stems.length}${counts ? ` ${counts}` : ''}`
    : `${label}${counts ? ` ${counts}` : ''}`

  const samplingRows =
    showStepper && activeIndex !== null ? (
      <div className="relative" data-testid="thread-run-activity-stepper">
        <div
          className="absolute bottom-3 left-[7px] top-3 w-px bg-border"
          aria-hidden
        />
        <ol className="space-y-3">
          {stems.map((stem, index) => {
            const state =
              index < activeIndex
                ? 'done'
                : index === activeIndex
                  ? 'active'
                  : 'pending'
            const doneCounts =
              state === 'done' && activity.progress?.total != null
                ? `${activity.progress.total} / ${activity.progress.total}`
                : null

            return (
              <li
                key={`${index}:${stem}`}
                className="relative z-[1] flex items-start gap-2.5"
                data-testid={`thread-run-activity-step-${index}`}
                data-state={state}
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center bg-background">
                  <ThreadStepGlyph state={state} testIdPrefix="thread-run-activity" />
                </span>
                <div className="min-w-0 flex-1">
                  {state === 'active' ? (
                    <Text size="sm" color="muted">
                      {label}
                      {counts ? (
                        <span data-testid="thread-run-activity-progress">
                          {` ${counts}`}
                        </span>
                      ) : null}
                    </Text>
                  ) : doneCounts ? (
                    <Text size="sm" color="muted">
                      {doneCounts}
                    </Text>
                  ) : null}
                  {stem ? (
                    <Text
                      size="xs"
                      color="muted"
                      className={cn(
                        'truncate',
                        state === 'pending' && 'text-muted-foreground/60',
                      )}
                      data-testid={`thread-run-activity-stem-${index}`}
                    >
                      {stem}
                    </Text>
                  ) : null}
                </div>
              </li>
            )
          })}
        </ol>
      </div>
    ) : null

  return (
    <div
      className={cn('flex gap-2.5', showStepper ? 'items-start' : 'items-center')}
      role="status"
      aria-live="polite"
      aria-label={ariaLabel.trim()}
      data-testid="thread-run-activity-indicator"
      data-phase={activity.phase}
    >
      {showStepper ? (
        samplingRows
      ) : (
        <>
          <TwinsMarkLoader
            className="h-4 w-4 text-muted-foreground"
            dataTestId="thread-run-activity-mark"
          />
          <Text size="sm" color="muted">
            {label}
            {counts ? (
              <span data-testid="thread-run-activity-progress">{` ${counts}`}</span>
            ) : null}
          </Text>
        </>
      )}
    </div>
  )
}
